// StorageHealthPanel — SuperAdmin view of platform retention + row counts.
// Reads /api/admin/storage-health (which mirrors the cron config so the
// numbers always match reality). Feb 14, 2026 PM session #5.

import { useEffect, useState } from "react";
import { HardDrive, Lock, Recycle, Warning, Sparkle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";

export default function StorageHealthPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/storage-health");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load storage health.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-6 text-sm text-[#6B6A68]">Loading…</div>;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="storage-health-panel">
      {/* Header */}
      <div className="bg-white border border-[#D8D6D2] rounded-lg p-5">
        <div className="flex items-start gap-3">
          <HardDrive size={24} weight="duotone" className="text-[#4A5D4E] shrink-0 mt-1" />
          <div className="flex-1">
            <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Storage Health</h2>
            <p className="text-sm text-[#6B6A68] mt-1 max-w-2xl">{data.principle}</p>
            <p className="text-[11px] text-[#A89360] mt-2 uppercase tracking-[0.15em] flex items-center gap-1.5">
              <Recycle size={11} /> Next sweep: {data.next_sweep_at}
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            className="text-xs px-3 py-1.5 rounded-md border border-[#D8D6D2] text-[#6B6A68] hover:text-[#1C1B1A] hover:bg-[#FAFAFA]"
            data-testid="storage-health-refresh"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Tracked (rolling deletion) */}
      <section data-testid="storage-health-tracked">
        <h3 className="text-xs uppercase tracking-[0.2em] text-[#6B6A68] mb-3 flex items-center gap-1.5">
          <Recycle size={12} /> Rolling deletion (platform data)
        </h3>
        <div className="bg-white border border-[#D8D6D2] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF7F2] border-b border-[#E8E5DF] text-[10px] uppercase tracking-[0.15em] text-[#6B6A68]">
              <tr>
                <th className="text-left px-3 py-2">Collection</th>
                <th className="text-left px-3 py-2">Policy</th>
                <th className="text-right px-3 py-2 tabular-nums">Total rows</th>
                <th className="text-right px-3 py-2 tabular-nums">Stale (next sweep)</th>
                <th className="text-left px-3 py-2">Pin exception</th>
              </tr>
            </thead>
            <tbody>
              {(data.tracked || []).map((it) => (
                <tr key={it.collection} className="border-b border-[#F0EDE7] last:border-0">
                  <td className="px-3 py-2 font-mono text-xs">{it.collection}</td>
                  <td className="px-3 py-2 text-[11px] text-[#6B6A68]">{it.policy}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{(it.total_rows || 0).toLocaleString()}</td>
                  <td className={`px-3 py-2 text-right tabular-nums ${(it.stale_rows_due_next_sweep || 0) > 0 ? "text-[#C16E5A] font-medium" : ""}`}>
                    {it.stale_rows_due_next_sweep === null
                      ? "—"
                      : it.stale_rows_due_next_sweep > 0
                        ? `↓ ${it.stale_rows_due_next_sweep.toLocaleString()}`
                        : "—"}
                  </td>
                  <td className="px-3 py-2 text-[10px] text-[#6B6A68]">
                    {it.has_pin_exception ? <span className="text-[#4A5D4E]">Pinned rows kept</span> : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-[#6B6A68] mt-2 italic">
          Stale rows flagged in orange will be deleted on the next 24h cron sweep.
        </p>
      </section>

      {/* Keep forever */}
      <section data-testid="storage-health-keepers">
        <h3 className="text-xs uppercase tracking-[0.2em] text-[#6B6A68] mb-3 flex items-center gap-1.5">
          <Lock size={12} /> Keep forever (silos, identity, payments, audit)
        </h3>
        <div className="bg-white border border-[#D8D6D2] rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF7F2] border-b border-[#E8E5DF] text-[10px] uppercase tracking-[0.15em] text-[#6B6A68]">
              <tr>
                <th className="text-left px-3 py-2">Collection</th>
                <th className="text-right px-3 py-2 tabular-nums">Total rows</th>
              </tr>
            </thead>
            <tbody>
              {(data.keep_forever || []).map((it) => (
                <tr key={it.collection} className="border-b border-[#F0EDE7] last:border-0">
                  <td className="px-3 py-2 font-mono text-xs">{it.collection}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{(it.total_rows || 0).toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <p className="text-[10px] text-[#6B6A68] flex items-center gap-1.5 italic">
        <Sparkle size={10} weight="duotone" /> Numbers are estimates (Mongo's `estimated_document_count`). Stale counts capped at 10k per collection for cheap scanning.
      </p>
    </div>
  );
}
